import { cn } from '@/lib/utils';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "./ui";



type ActionDropdownProps = {
  menuModal?: boolean;
  children: ReactNode;
};

const ActionDropdown = ({
  menuModal = true,
  children,
}: ActionDropdownProps) => {
  return <DropdownMenu modal={menuModal}>{children}</DropdownMenu>;
};

type ActionDropdownTriggerProps = {
  triggerContent: ReactNode;
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon' | 'icon-sm' | 'icon-lg';
  buttonVariant?: 'ghost' | 'outline' | 'secondary';
  ariaLabel?: string;
  className?: {
    triggerClass?: string;
    buttonClass?: string;
  };
} & Omit<ComponentPropsWithoutRef<typeof Button>, 'children' | 'asChild' | 'className'>;

const ActionDropdownTrigger = ({
  triggerContent,
  buttonSize = 'icon',
  buttonVariant = 'ghost',
  ariaLabel = 'Open Menu',
  className,
  ...props
}: ActionDropdownTriggerProps) => {
  const { triggerClass, buttonClass } = className ?? {};
  return (
    <DropdownMenuTrigger asChild className={cn('', triggerClass)}>
      <Button
        variant={buttonVariant}
        className={cn('hover:cursor-pointer', buttonClass)}
        aria-label={ariaLabel}
        size={buttonSize}
        {...props}
      >
        {triggerContent}
      </Button>
    </DropdownMenuTrigger>
  );
};

type ActionDropdownContentProps = {
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  className?: string;
  dropdownShape: DropdownNode[];
};

const ActionDropdownContent = ({
  align = 'start',
  sideOffset = 4,
  className,
  dropdownShape,
}: ActionDropdownContentProps) => {
  return (
    <DropdownMenuContent
      align={align}
      sideOffset={sideOffset}
      className={cn('', className)}
    >
      {dropdownShape.map((item, index) => renderNode(item, index))}
    </DropdownMenuContent>
  );
};

interface DropdownBase {
  id?: string;
  _id?: string;
  disabled?: boolean;
  className?: string;
}

interface DropdownItem extends DropdownBase {
  type: 'item';
  label: string;
  icon?: ReactNode;
  iconClass?: string;
  shortcut?: ReactNode;
  shortcutClass?: string;
  onSelect?: () => void;
}

export type DropdownNode =
  | DropdownItem
  | DropdownSubmenu
  | DropdownGroup
  | DropdownSeparator;

export interface DropdownGroup extends DropdownBase {
  type: 'group';
  label?: string;
  labelClass?: string;
  children: DropdownNode[];
}

export interface DropdownSubmenu extends DropdownBase {
  type: 'submenu';
  label: string;
  icon?: ReactNode;
  iconClass?: string;
  shortcut?: ReactNode;
  shortcutClass?: string;
  subTriggerClass?: string;
  subContentClass?: string;
  children: DropdownNode[];
}

export interface DropdownSeparator {
  type: 'separator';
  id?: string;
  className?: string;
}

const isSimpleOption = (item: DropdownNode): item is DropdownItem => {
  return item.type === 'item';
};

const isSeparator = (item: DropdownNode): item is DropdownSeparator => {
  return item.type === 'separator';
};

const isOptionWithSubmenu = (item: DropdownNode): item is DropdownSubmenu => {
  return item.type === 'submenu';
};

const isGroupOption = (item: DropdownNode): item is DropdownGroup => {
  return item.type === 'group';
};

interface MenuIconProps {
  icon: ReactNode;
  className?: string;
}

const MenuIcon = ({ icon, className }: MenuIconProps) => {
  return (
    <span
      className={cn('mr-2 flex h-4 w-4 items-center justify-center', className)}
    >
      {icon}
    </span>
  );
};

const renderDropdownItems = (options: DropdownItem, index: number) => {
  return (
    <DropdownMenuItem
      key={options._id || options.id || `${options.label}-${index}`}
      className={cn('', options.className)}
      disabled={options.disabled}
      onSelect={options.onSelect}
    >
      {options.icon && (
        <MenuIcon icon={options.icon} className={options.iconClass} />
      )}

      {options.label}

      {options.shortcut && (
        <DropdownMenuShortcut className={cn('', options.shortcutClass)}>
          {options.shortcut}
        </DropdownMenuShortcut>
      )}
    </DropdownMenuItem>
  );
};

const renderSeparator = (node: DropdownSeparator, index: number) => {
  return (
    <DropdownMenuSeparator
      key={`separator-${node?.id ?? index}`}
      className={cn('', node?.className)}
    />
  );
};

const renderGroupOptions = (group: DropdownGroup, index: number) => {
  return (
    <DropdownMenuGroup
      key={`group-${group.label ?? index}`}
      className={cn('', group.className)}
    >
      {group.label && (
        <DropdownMenuLabel className={group.labelClass}>
          {group.label}
        </DropdownMenuLabel>
      )}

      {group.children.map((child, childIndex) => renderNode(child, childIndex))}
    </DropdownMenuGroup>
  );
};

const renderSubmenuOptions = (
  submenuOptions: DropdownSubmenu,
  index: number,
) => {
  return (
    <DropdownMenuSub key={`submenu-${submenuOptions.label}-${index}`}>
      <DropdownMenuSubTrigger
        className={submenuOptions.subTriggerClass}
        disabled={submenuOptions.disabled}
      >
        {submenuOptions.icon && (
          <MenuIcon
            icon={submenuOptions.icon}
            className={submenuOptions.iconClass}
          />
        )}
        {submenuOptions.label}
        {submenuOptions.shortcut && (
          <DropdownMenuShortcut className={submenuOptions.shortcutClass}>
            {submenuOptions.shortcut}
          </DropdownMenuShortcut>
        )}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className={submenuOptions.subContentClass}>
          {submenuOptions.children.map((subItem, subIndex) =>
            renderNode(subItem, subIndex),
          )}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};

const renderNode = (node: DropdownNode, index: number) => {
  if (isSimpleOption(node)) {
    return renderDropdownItems(node, index);
  }

  if (isGroupOption(node)) {
    return renderGroupOptions(node, index);
  }

  if (isOptionWithSubmenu(node)) {
    return renderSubmenuOptions(node, index);
  }

  if (isSeparator(node)) {
    return renderSeparator(node, index);
  }

  return null;
};

export { ActionDropdown, ActionDropdownContent, ActionDropdownTrigger };

// classname in each object and separator not rendering
